import React, { useState, useEffect, useRef } from 'react';
import Icon from '../shared/Icon';
import './Login.css';
import LoginCard from './LoginCard';
import OtpVerification from '../OtpVerification/OtpVerification';
import { apiService } from '../../../services/api';
import { isRememberEmailEnabled, getRememberedEmail, clearRememberedEmail, getInflightEmail, setInflightEmail, clearInflightEmail } from '../../../utils/rememberEmail';

function Login({ backupLoginEnabled = false }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [remembered, setRemembered] = useState(false);
  const emailRef = useRef(null);

  // R1/R4/R6: pre-fill the remembered email when the setting is enabled;
  // when disabled, drop any stale stored value and start empty.
  useEffect(() => {
    if (isRememberEmailEnabled()) {
      // Prefer the durable value (written only on successful auth); fall back to
      // the in-flight value from a submit that's mid-SSO-redirect this session.
      const saved = getRememberedEmail() || getInflightEmail();
      if (saved) {
        setEmail(saved);
        setRemembered(true);
      }
    } else {
      clearRememberedEmail();
      clearInflightEmail();
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorType = params.get('error');
    const msg = params.get('message');
    let decodedMsg = '';
    if (msg) {
      try {
        decodedMsg = decodeURIComponent(msg);
      } catch (_) {
        decodedMsg = msg;
      }
    }
    if (errorType === 'owner_must_add_you') {
      setError(decodedMsg || 'To Access the application Owner needs to add you as a User, Kindly contact your admin.');
    } else if (errorType === 'wrong_account') {
      setError(decodedMsg || 'You signed in with a different Microsoft account. Please sign out of Microsoft or choose the account for the email you entered.');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Please enter your work email.');
      return;
    }
    if (!trimmed.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    // Keep the entered email for THIS tab/session only (sessionStorage) so it
    // survives the SSO redirect / reload. It is NOT persisted across users on a
    // shared browser — the durable remember happens only on successful auth (App.js).
    if (isRememberEmailEnabled()) setInflightEmail(trimmed);
    setLoading(true);
    try {
      const data = await apiService.loginStart(trimmed);

      if (data.auth_method === 'otp') {
        if (!backupLoginEnabled) {
          setError('SSO is not configured for your organization. Contact your administrator to set up SSO.');
          return;
        }
        setShowOtp(true);
        setError('');
        return;
      }

      if (data.redirect_url) {
        window.location.href = data.redirect_url;
        return;
      }
      setError('Organization not registered or not configured for SSO. Contact your administrator to set up SSO.');
    } catch (err) {
      setError(err.message || 'Unable to start sign-in. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  if (showOtp) {
    return (
      <LoginCard>
        {/* No back-to-email path by design (John's OTP template 969-1490 has only
            "Request a new code" and "Contact support" — no return-to-email link). */}
        <OtpVerification email={email} />
      </LoginCard>
    );
  }

  return (
    <LoginCard>
      {error && (
        <div className="resui-banner resui-banner--error" role="alert">
          <Icon name="error" />
          <span className="resui-banner-text">{error}</span>
        </div>
      )}
      <form onSubmit={handleSubmit} className="login-form">
        <input
          ref={emailRef}
          type="email"
          className={`resui-input${error ? ' resui-input--error' : ''}`}
          placeholder="Enter your work email"
          aria-label="Work email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError('');
            if (remembered) {
              // Diverging from the pre-filled value — drop the stored copies
              // so a reload can't re-pre-fill the stale address (keeps UI +
              // storage in sync).
              clearRememberedEmail();
              clearInflightEmail();
              setRemembered(false);
            }
          }}
          disabled={loading}
          autoComplete="email"
          autoFocus
        />
        <button
          type="submit"
          className="resui-btn resui-btn--primary login-submit-btn"
          disabled={loading}
        >
          {loading ? 'Redirecting…' : 'Sign in with your organization'}
        </button>
      </form>
    </LoginCard>
  );
}

export default Login;
